import {
  DOCUMENT_SOURCE_ERRORS,
  MICROSOFT365_IMPORT_MAX,
  zonedDaysBetween,
  zonedParts,
  type DocumentDto,
  type DocumentScope,
  type DocumentSourceCheckDto,
  type DocumentSourceDto,
  type Microsoft365BrowseAt,
  type Microsoft365BrowseDto,
  type Microsoft365Feature,
  type Microsoft365FileBlock,
  type Microsoft365FileRowDto,
  type Microsoft365ImportFailure,
  type Microsoft365ImportItem,
  type Microsoft365ImportResultDto,
  type Microsoft365ImportTarget,
  type Microsoft365SavedAttachmentDto,
  type Microsoft365Source,
  type Microsoft365SourceKey,
  type Microsoft365StatusDto,
  type TenantRole,
  type WorkspaceDto,
} from '@dbrain/shared';
import { formatBytes, numberLocative, trLocative } from './format.js';

/**
 * Microsoft 365 part 3 on the web: documents from OneDrive, SharePoint and Outlook attachments (DOC-017…; MsEkleMenu,
 * MsBelgeSec, MsMailEkKaydet, MsBelgelerKaynak, MsDurumlar). DOM-free, so the rules are decided and tested in one
 * place: which sources the "+" menu offers, what the picker shows for the person's state, at most 20 files at a time,
 * why a row cannot be picked, where the files may go (by role), the warning and its "Anladım", the footer, the
 * source wording, the import's result card, and Belgeler's source states and "Yeniden al". Texts are i18n keys under
 * `microsoft365.files` and `documents`.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

const K = 'microsoft365.files';

// ---- sources and the person's state -------------------------------------------------------------------------

/** The three places files come from, in the mockups' order. */
export const SOURCES: readonly Microsoft365Source[] = ['onedrive', 'sharepoint', 'outlook'];

/** The switch each source follows; Outlook attachments follow "Mail okuma" (they have no switch of their own). */
export const SOURCE_FEATURE: Record<Microsoft365Source, Microsoft365Feature> = {
  onedrive: 'onedrive_import',
  sharepoint: 'sharepoint_import',
  outlook: 'mail_read',
};

/** Where a source opens: My OneDrive (and what was shared with me, by search), the sites, the mails with attachments. */
export function startOf(source: Microsoft365Source): Microsoft365BrowseAt {
  if (source === 'onedrive') return { kind: 'onedrive' };
  if (source === 'sharepoint') return { kind: 'sites' };
  return { kind: 'outlook' };
}

/** The features an upgrade sign-in names for a source; Outlook attachments need nothing beyond reading mail. */
export function featuresFor(source: Microsoft365Source): Microsoft365Feature[] {
  return source === 'outlook' ? [] : [SOURCE_FEATURE[source]];
}

/** The person may use Microsoft 365 now: the firm and the team allow it and their own switch is on. */
const usable = (status: Microsoft365StatusDto | undefined): status is Microsoft365StatusDto => Boolean(status && status.available && status.enabled);

export interface AddMenuView {
  /** OneDrive, SharePoint, Outlook ekleri: each only while its switch is on */
  sources: Microsoft365Source[];
  /** the connected account, in the group's header */
  email: string | null;
}

/**
 * The "+" menu's MICROSOFT 365 group (MsEkleMenu): only in Chat and Cowork (D-199: Microsoft 365 is chat-only), only
 * when the person may use it, each item only when its switch is on; null when there is nothing to offer (a personal
 * space, turned off for the firm or the team, the person's own switch off: MsDurumlar "+ menüsünde Microsoft grubu
 * görünmez").
 */
export function addMenuView(status: Microsoft365StatusDto | undefined, experience: 'chat' | 'code'): AddMenuView | null {
  if (experience !== 'chat' || !usable(status)) return null;
  const sources = SOURCES.filter((source) => status.features[SOURCE_FEATURE[source]]);
  return sources.length > 0 ? { sources, email: status.account?.email ?? null } : null;
}

/**
 * What the picker shows for a source: its files, or a panel that says what is missing. `scopes`: the switch is on but
 * the account has not granted its permission yet ("Yeni izin gerekiyor · yeniden bağlan").
 */
export type Access = 'ready' | 'connect' | 'reconnect' | 'consent' | 'scopes' | 'off' | 'unavailable';

export function sourceAccess(status: Microsoft365StatusDto, source: Microsoft365Source): Access {
  const feature = SOURCE_FEATURE[source];
  if (!usable(status) || !status.features[feature]) return 'off';
  switch (status.state) {
    case 'not_connected':
      return 'connect';
    case 'needs_reconnect':
      return 'reconnect';
    case 'consent_required':
      return 'consent';
    case 'connected':
      return status.grants[feature] ? 'ready' : 'scopes';
    default:
      return 'off';
  }
}

/** A browse or import the server refused for the person's Microsoft 365 state: the panel to show instead. */
export function errorAccess(code: string | undefined): Access | null {
  switch (code) {
    case 'microsoft_not_connected':
      return 'connect';
    case 'microsoft_reconnect':
      return 'reconnect';
    case 'microsoft_consent_required':
      return 'consent';
    case 'microsoft_scopes_required':
      return 'scopes';
    case 'microsoft_not_available':
    case 'microsoft_not_configured':
      return 'off';
    case 'microsoft_unavailable':
      return 'unavailable';
    default:
      return null;
  }
}

/** "Belgelerime kaydet" is offered: Microsoft 365 usable and mail reading on (MsMailEkKaydet). */
export function canSaveAttachments(status: Microsoft365StatusDto | undefined): boolean {
  return usable(status) && status.features.mail_read;
}

// ---- the way back after a sign-in ---------------------------------------------------------------------------

/** The address value that reopens the picker on a source after Microsoft's page (`returnTo`). */
export const PICKER_PARAM = 'm365belge';

/** `returnTo` for an upgrade sign-in from the picker: the page's own path, which reopens it on the same source. */
export function pickerReturnTo(path: string, source: Microsoft365Source): string {
  const url = new URL(path.startsWith('/') ? path : `/${path}`, 'http://oppy.invalid');
  url.searchParams.set(PICKER_PARAM, source);
  return `${url.pathname}${url.search}`;
}

export function pickerSourceOf(params: URLSearchParams): Microsoft365Source | null {
  const value = params.get(PICKER_PARAM);
  return (SOURCES as readonly string[]).includes(value ?? '') ? (value as Microsoft365Source) : null;
}

// ---- the picker's rows and the selection --------------------------------------------------------------------

/** One file, the same whether it was reached by a folder, a search or another page. */
export const fileId = (key: Microsoft365SourceKey): string => (key.kind === 'drive' ? `d:${key.driveId}:${key.itemId}` : `a:${key.messageId}:${key.attachmentId}`);

/** A picked file, kept while the person moves between folders and sources: the footer counts and sums these. */
export interface PickedFile {
  id: string;
  item: Microsoft365ImportItem;
  name: string;
  size: number | null;
}

/**
 * Why a file cannot be picked: the gateway's reason, or a size over the copy's limit (`maxMb`, the server's
 * UPLOAD_MAX_MB, which the picker's words quote; the gateway only knows its own, higher download cap).
 */
export function blockOfRow(row: Microsoft365FileRowDto, maxMb: number): Microsoft365FileBlock | null {
  if (row.blocked) return row.blocked;
  return row.kind === 'file' && row.size !== null && row.size > maxMb * 1024 * 1024 ? 'too_large' : null;
}

/** A row that can be picked (an importable file of the view's source, within the size limit); null otherwise. */
export function pickedOf(row: Microsoft365FileRowDto, source: Microsoft365Source, maxMb: number): PickedFile | null {
  if (row.kind !== 'file' || blockOfRow(row, maxMb) || !row.key) return null;
  // Outlook items are attachments, OneDrive and SharePoint items drive items (the import body's rule)
  if ((source === 'outlook') !== (row.key.kind === 'attachment')) return null;
  return { id: fileId(row.key), item: { source, key: row.key }, name: row.name, size: row.size };
}

export type RowView =
  /** a folder, a site or a library: its name opens it */
  | { kind: 'open' }
  /** nothing to do with it (a row the picker does not understand) */
  | { kind: 'inert' }
  /** greyed, with the reason in words */
  | { kind: 'blocked'; block: Microsoft365FileBlock }
  /** `full`: 20 are picked already, so this one waits until another is let go */
  | { kind: 'file'; selected: boolean; full: boolean };

export function rowView(row: Microsoft365FileRowDto, source: Microsoft365Source, picked: ReadonlyMap<string, PickedFile>, maxMb: number): RowView {
  if (row.kind !== 'file') return row.open ? { kind: 'open' } : { kind: 'inert' };
  const block = blockOfRow(row, maxMb);
  if (block) return { kind: 'blocked', block };
  const file = pickedOf(row, source, maxMb);
  if (!file) return { kind: 'inert' };
  const selected = picked.has(file.id);
  return { kind: 'file', selected, full: !selected && picked.size >= MICROSOFT365_IMPORT_MAX };
}

/** Picks or lets go of a file; null when picking would pass the limit of 20 at a time. */
export function togglePicked(picked: ReadonlyMap<string, PickedFile>, file: PickedFile, on: boolean): ReadonlyMap<string, PickedFile> | null {
  const next = new Map(picked);
  if (!on) {
    next.delete(file.id);
    return next;
  }
  if (picked.has(file.id)) return picked;
  if (picked.size >= MICROSOFT365_IMPORT_MAX) return null;
  next.set(file.id, file);
  return next;
}

export function pickedTotals(picked: ReadonlyMap<string, PickedFile>): { count: number; bytes: number } {
  let bytes = 0;
  for (const file of picked.values()) bytes += file.size ?? 0;
  return { count: picked.size, bytes };
}

/** The footer: "**3 belge seçildi** · 4,1 MB" (the lead is bold), or "Belge seçilmedi". */
export function footerParts(count: number, bytes: number, locale: string, t: Translate): { lead: string; size: string | null } {
  if (count === 0) return { lead: t(`${K}.picker.none`), size: null };
  return { lead: t(`${K}.picker.selected`, { count }), size: formatBytes(bytes, locale) };
}

/** The primary button: "3 belgeyi ekle"; "Ekle" (disabled) before anything is picked. */
export function addLabel(count: number, t: Translate): string {
  return count === 0 ? t(`${K}.picker.addNone`) : t(`${K}.picker.add`, { count });
}

/** All pages of a view as one list; the SharePoint start shows the followed sites when it has no rows of its own. */
export function viewRows(pages: readonly Microsoft365BrowseDto[]): Microsoft365FileRowDto[] {
  const rows = pages.flatMap((page) => page.rows);
  const first = pages[0];
  if (rows.length > 0 || !first || first.at.kind !== 'sites' || !first.sites) return rows;
  return first.sites.map((site) => ({
    kind: 'site' as const,
    name: site.name,
    by: null,
    at: null,
    size: null,
    childCount: null,
    fileKind: null,
    blocked: null,
    shared: false,
    context: null,
    key: null,
    open: site.at,
  }));
}

export type SortKey = 'name' | 'by' | 'at' | 'size';
export interface RowSort {
  key: SortKey;
  desc: boolean;
}

/** MsBelgeSec: "Değiştirilme", newest first. */
export const DEFAULT_SORT: RowSort = { key: 'at', desc: true };

/** A click on the sorted column turns it around; another column starts newest/largest first, or A to Z for names. */
export function nextSort(sort: RowSort, key: SortKey): RowSort {
  if (sort.key === key) return { key, desc: !sort.desc };
  return { key, desc: key === 'at' || key === 'size' };
}

/**
 * Folders (and sites, libraries) first, by name; then the files by the chosen column. Graph may ignore an order on
 * a business drive, so the loaded rows are sorted here; a missing value goes last either way.
 */
export function sortRows(rows: readonly Microsoft365FileRowDto[], sort: RowSort, locale: string): Microsoft365FileRowDto[] {
  const byName = (a: Microsoft365FileRowDto, b: Microsoft365FileRowDto) => a.name.localeCompare(b.name, locale, { sensitivity: 'base', numeric: true });
  const value = (row: Microsoft365FileRowDto): string | number | null => {
    switch (sort.key) {
      case 'name':
        return row.name;
      case 'by':
        return row.by;
      case 'at':
        return row.at ? Date.parse(row.at) : null;
      case 'size':
        return row.size;
    }
  };
  const files = (a: Microsoft365FileRowDto, b: Microsoft365FileRowDto) => {
    const x = value(a);
    const y = value(b);
    if (x === null || y === null || Number.isNaN(x) || Number.isNaN(y)) {
      const missing = Number(x === null || Number.isNaN(x)) - Number(y === null || Number.isNaN(y));
      return missing || byName(a, b);
    }
    const order = typeof x === 'string' && typeof y === 'string' ? x.localeCompare(y, locale, { sensitivity: 'base', numeric: true }) : Number(x) - Number(y);
    return (sort.desc ? -order : order) || byName(a, b);
  };
  const containers = rows.filter((row) => row.kind !== 'file').sort((a, b) => (sort.key === 'name' && sort.desc ? byName(b, a) : byName(a, b)));
  return [...containers, ...rows.filter((row) => row.kind === 'file').sort(files)];
}

/** The search box's words for a view (`picker.search.<key>`); null where there is nothing to search (a site's libraries). */
export function searchKey(at: Microsoft365BrowseAt, source: Microsoft365Source): 'library' | 'folder' | 'onedrive' | 'sites' | 'outlook' | null {
  switch (at.kind) {
    case 'onedrive':
      return 'onedrive';
    case 'sites':
      return 'sites';
    case 'outlook':
      return 'outlook';
    case 'site':
      return null;
    case 'folder':
      if (at.itemId !== null) return 'folder';
      return source === 'sharepoint' ? 'library' : 'onedrive';
  }
}

/** What an empty view says (`picker.empty.<key>`). */
export function emptyKey(at: Microsoft365BrowseAt, query: string): 'search' | 'outlook' | 'sites' | 'site' | 'folder' {
  if (query.trim()) return 'search';
  if (at.kind === 'outlook' || at.kind === 'sites' || at.kind === 'site') return at.kind;
  return 'folder';
}

/** The same view (the left nav's current site, the crumbs). */
export function sameAt(a: Microsoft365BrowseAt | null | undefined, b: Microsoft365BrowseAt | null | undefined): boolean {
  if (!a || !b || a.kind !== b.kind) return false;
  if (a.kind === 'site' && b.kind === 'site') return a.siteId === b.siteId;
  if (a.kind === 'folder' && b.kind === 'folder') return a.driveId === b.driveId && a.itemId === b.itemId;
  return true;
}

/** The site a view belongs to (from its crumbs), to mark it in "SİTELER". */
export function siteOf(view: Pick<Microsoft365BrowseDto, 'at' | 'crumbs'> | undefined): string | null {
  if (!view) return null;
  if (view.at.kind === 'site') return view.at.siteId;
  for (const crumb of view.crumbs) if (crumb.at?.kind === 'site') return crumb.at.siteId;
  return null;
}

// ---- where the files go ("Nereye eklensin?") ----------------------------------------------------------------

export type Target =
  | { kind: 'user' }
  /** a team's shared workspace (a directory domain) */
  | { kind: 'team'; workspaceId: string; name: string }
  /** another workspace the person may write in (taslaksız) */
  | { kind: 'workspace'; workspaceId: string; name: string }
  | { kind: 'organization' }
  /** "Bu sohbet": only from a composer; the files go one by one like attachments */
  | { kind: 'conversation' };

export const targetId = (target: Target): string => (target.kind === 'team' || target.kind === 'workspace' ? target.workspaceId : target.kind);

export interface TargetList {
  options: Target[];
  /** Herkes is not for this person: the locked line "Herkes: yalnız süper yöneticiler" */
  organizationLocked: boolean;
}

/** Herkes is for the firm's top admins only (the server allows the tenant's owner and admin: the super admin). */
export const mayShareWithEveryone = (role: TenantRole | undefined): boolean => role === 'owner' || role === 'admin';

/**
 * The places the person may put files: Kişisel (default), each of their teams, other workspaces they can write in,
 * "Bu sohbet" from a composer, and Herkes for a super admin. The person's own chat workspace ("Kişisel" of v0.15e.2)
 * is not a place of its own: Kişisel is.
 */
export function targetsFor(input: { role: TenantRole | undefined; workspaces: readonly WorkspaceDto[]; conversation: boolean }, locale = 'tr'): TargetList {
  const writable = input.workspaces.filter((w) => w.role !== 'viewer' && !w.personal);
  const byName = (a: WorkspaceDto, b: WorkspaceDto) => a.name.localeCompare(b.name, locale);
  const everyone = mayShareWithEveryone(input.role);
  return {
    options: [
      { kind: 'user' },
      ...writable
        .filter((w) => w.directoryManaged)
        .sort(byName)
        .map((w): Target => ({ kind: 'team', workspaceId: w.id, name: w.name })),
      ...writable
        .filter((w) => !w.directoryManaged)
        .sort(byName)
        .map((w): Target => ({ kind: 'workspace', workspaceId: w.id, name: w.name })),
      ...(input.conversation ? [{ kind: 'conversation' } as const] : []),
      ...(everyone ? [{ kind: 'organization' } as const] : []),
    ],
    organizationLocked: !everyone,
  };
}

/** Everyone there sees the files, also people who cannot see them in Microsoft 365: the warning and "Anladım". */
export const needsAcknowledgement = (target: Target): boolean => target.kind === 'team' || target.kind === 'workspace' || target.kind === 'organization';

/** The import body's target; null while "Anladım" is missing, and for "Bu sohbet" (its own route). */
export function importTargetOf(target: Target, acknowledged: boolean): Microsoft365ImportTarget | null {
  switch (target.kind) {
    case 'user':
      return { scope: 'user' };
    case 'team':
    case 'workspace':
      return acknowledged ? { scope: 'workspace', workspaceId: target.workspaceId, acknowledged: true } : null;
    case 'organization':
      return acknowledged ? { scope: 'organization', acknowledged: true } : null;
    case 'conversation':
      return null;
  }
}

/** The primary button works: something picked, and the place's warning acknowledged where it has one. */
export function canAdd(count: number, target: Target, acknowledged: boolean): boolean {
  if (count === 0 || count > MICROSOFT365_IMPORT_MAX) return false;
  return target.kind === 'conversation' || importTargetOf(target, acknowledged) !== null;
}

/** "Kişisel", "Ekip: Finans", "Çalışma alanı: Satış", "Herkes", "Bu sohbet" */
export function placeLabel(target: Target, t: Translate): string {
  switch (target.kind) {
    case 'team':
      return t(`${K}.targets.team`, { name: target.name });
    case 'workspace':
      return t(`${K}.targets.workspace`, { name: target.name });
    default:
      return t(`${K}.targets.${target.kind}`);
  }
}

/** Where a document is, as a place of the picker (a saved attachment's "Kaydedildi · Ekip: Finans"). */
export function targetOfPlace(scope: DocumentScope, workspaceId: string | null, workspaces: readonly WorkspaceDto[]): Target {
  if (scope === 'organization') return { kind: 'organization' };
  if (scope === 'conversation' || scope === 'upload') return { kind: 'conversation' };
  if (scope === 'workspace' && workspaceId) {
    const workspace = workspaces.find((w) => w.id === workspaceId);
    if (workspace) return { kind: workspace.directoryManaged ? 'team' : 'workspace', workspaceId, name: workspace.name };
    return { kind: 'workspace', workspaceId, name: '' };
  }
  return { kind: 'user' };
}

/** Whose files they are, for the warning's ending; several sources together get one general ending. */
export type SourceWording = Microsoft365Source | 'mixed';

export function sourceWording(sources: Iterable<Microsoft365Source>, fallback: Microsoft365Source): SourceWording {
  const set = new Set(sources);
  if (set.size === 0) return fallback;
  if (set.size > 1) return 'mixed';
  return [...set][0] ?? fallback;
}

/**
 * The amber warning of a shared place (MsBelgeSec): "Finans ekibindeki herkes görür, SharePoint'te bu dosyaları
 * göremeyenler de." (OneDrive'da …; an attachment: "… bu maili almayanlar da."); null for Kişisel and Bu sohbet.
 */
export function warningText(target: Target, wording: SourceWording, t: Translate): string | null {
  if (!needsAcknowledgement(target)) return null;
  const tail = t(`${K}.targets.tail.${wording}`);
  if (target.kind === 'organization') return t(`${K}.targets.warning.organization`, { tail });
  return t(`${K}.targets.warning.${target.kind}`, { name: target.kind === 'team' || target.kind === 'workspace' ? target.name : '', tail });
}

/** The aside's bottom note: "Üstünde Kaynak: SharePoint yazar…"; attachments do not change, so theirs has no "Yeniden al". */
export function copyNote(sources: Iterable<Microsoft365Source>, fallback: Microsoft365Source, t: Translate): { key: string; source: string } {
  const set = new Set(sources);
  if (set.size === 0) set.add(fallback);
  if (set.size === 1 && set.has('outlook')) return { key: `${K}.picker.copyNoteOutlook`, source: 'Outlook' };
  const names = SOURCES.filter((source) => set.has(source)).map((source) => (source === 'outlook' ? 'Outlook' : t(`${K}.sources.${source}`)));
  return { key: `${K}.picker.copyNote`, source: names.join(', ') };
}

// ---- the import's answer (MsDurumlar "Belge alınamadı") -----------------------------------------------------

/** A picked file that was not added, in plain words ("bu dosyayı görme izniniz kalmamış."). */
export function failureReason(code: Microsoft365ImportFailure | string, maxMb: number, t: Translate): string {
  return t(`${K}.result.reasons.${code}`, { mb: maxMb, defaultValue: t(`${K}.result.reasons.unavailable`) });
}

export interface ResultView {
  failed: { name: string; reason: string }[];
  /** new copies */
  added: number;
  /** already in that place: nothing copied again ("zaten … içindeydi") */
  duplicates: number;
}

/** The result in the card's terms; a failure without a name takes the picked file's name, else "Bir dosya". */
export function resultView(result: Microsoft365ImportResultDto, names: readonly string[], maxMb: number, t: Translate): ResultView {
  return {
    failed: result.failed.map((item) => ({ name: item.name ?? names[item.index] ?? t(`${K}.result.unnamed`), reason: failureReason(item.code, maxMb, t) })),
    added: result.added.filter((item) => !item.duplicate).length,
    duplicates: result.added.filter((item) => item.duplicate).length,
  };
}

// ---- Belgeler: where a copy came from and how it stands -----------------------------------------------------

/** Belgeler's "Kaynak": "SharePoint · Finans", "OneDrive", "Outlook eki", or "Bilgisayardan" for an upload. */
export function sourceLabel(source: DocumentSourceDto | null | undefined, t: Translate): string {
  if (!source) return t('documents.source.upload');
  if (source.provider === 'sharepoint') return source.siteName ? t('documents.source.sharepointSite', { site: source.siteName }) : t('documents.source.sharepoint');
  return t(`documents.source.${source.provider}`);
}

/** The aside's box: the path as names ("SharePoint › Finans › Belgeler"), else the label. Never a URL. */
export function sourcePath(source: DocumentSourceDto, t: Translate): string {
  if (source.path?.trim()) return source.path;
  if (source.provider === 'outlook') return source.senderName ? t('documents.source.outlookFrom', { sender: source.senderName }) : t('documents.source.outlook');
  return sourceLabel(source, t);
}

/** "Kaynak: Tümü ▾": `upload` stands for a document without a source. */
export const sourceFilterOf = (doc: Pick<DocumentDto, 'source'>): Microsoft365Source | 'upload' => doc.source?.provider ?? 'upload';

const SOURCE_ERRORS = new Set<string>(DOCUMENT_SOURCE_ERRORS);

/** A copy fetched now: the first one ("İçe aktarılıyor") or "Yeniden al" over a working copy ("Yeniden alınıyor…"). */
const fetching = (doc: DocumentDto): boolean => doc.stage === 'importing' && (doc.status === 'queued' || doc.status === 'processing');

export type SourceStateKind = 'importing' | 'refetching' | 'import_failed' | 'refetch_failed' | 'changed' | 'gone' | 'unchanged' | 'updated';

export interface SourceState {
  kind: SourceStateKind;
  /** a failure's code (`documents.errors.<code>`) */
  code?: string;
}

/** How a "Yeniden al" that the person started here ended. */
export type RefetchOutcome = 'updated' | 'unchanged' | 'failed';

/**
 * Belgeler's state of a Microsoft 365 copy (MsBelgelerKaynak): being imported, could not be imported, being fetched
 * again, the last fetch failed (the old copy stays), changed or gone at the source (the viewer's own check: never
 * stored, so a team member who cannot see the file in Microsoft 365 never sees it), or how a "Yeniden al" started
 * here ended. Null: the plain document states apply (Hazır, İşleniyor, Okunamadı).
 */
export function sourceState(doc: DocumentDto, check: DocumentSourceCheckDto | undefined, outcome?: RefetchOutcome | null): SourceState | null {
  const source = doc.source;
  if (!source) return null;
  if (fetching(doc)) return { kind: source.fetchedAt ? 'refetching' : 'importing' };
  if (doc.status === 'failed') {
    return doc.stage === 'importing' || SOURCE_ERRORS.has(doc.errorCode ?? '') ? { kind: 'import_failed', code: doc.errorCode ?? 'source_unavailable' } : null;
  }
  if (doc.status !== 'ready') return null;
  if (outcome === 'failed' || (source.lastError && outcome !== 'updated' && outcome !== 'unchanged')) return { kind: 'refetch_failed', code: source.lastError ?? 'source_unavailable' };
  if (outcome === 'unchanged') return { kind: 'unchanged' };
  if (outcome === 'updated') return { kind: 'updated' };
  if (check?.state === 'changed') return { kind: 'changed' };
  if (check?.state === 'gone') return { kind: 'gone' };
  return null;
}

/**
 * Following a "Yeniden al" started here: the copy goes to "importing" and back. The request cleared the source's last
 * error, so an error there once the copy is back is this fetch's; back without one and with the same processing
 * time, nothing was copied (Microsoft answered "not modified"): "Kaynakta değişiklik yok". Only the server's own
 * values are compared with each other, never with this browser's clock (it may be ahead or behind).
 */
export interface RefetchWatch {
  processedAt: string | null;
  /** the copy's updatedAt when "Yeniden al" was pressed (the request changes it) */
  updatedAt: string;
  seenBusy: boolean;
}

export function refetchStep(watch: RefetchWatch, doc: DocumentDto): { watch: RefetchWatch; outcome: RefetchOutcome | null } {
  if (fetching(doc)) return { watch: watch.seenBusy ? watch : { ...watch, seenBusy: true }, outcome: null };
  // a list read before the request was seen still shows the copy as it was (an earlier fetch's error too)
  if (!watch.seenBusy && doc.updatedAt === watch.updatedAt) return { watch, outcome: null };
  if (doc.status === 'failed' || doc.source?.lastError) return { watch, outcome: 'failed' };
  return { watch, outcome: doc.processedAt === watch.processedAt ? 'unchanged' : 'updated' };
}

/** The ready OneDrive and SharePoint copies on screen whose source the viewer may check with their own account (≤ 50). */
export function checkIdsOf(docs: readonly DocumentDto[], status: Microsoft365StatusDto | undefined): string[] {
  if (!usable(status) || status.state !== 'connected') return [];
  const may = (source: Microsoft365Source) => source !== 'outlook' && status.features[SOURCE_FEATURE[source]] && status.grants[SOURCE_FEATURE[source]];
  return docs
    .filter((doc) => doc.status === 'ready' && doc.stage !== 'importing' && doc.source && may(doc.source.provider))
    .map((doc) => doc.id)
    .slice(0, 50);
}

export type RefetchView =
  /** not a copy this person may fetch again (an upload, not theirs to manage) */
  | { kind: 'hidden' }
  /** "Mail ekleri değişmez": no button */
  | { kind: 'outlook' }
  /** being copied now */
  | { kind: 'busy' }
  | { kind: 'ready' }
  /** "Yeniden almak için Microsoft hesabınızı bağlayın" */
  | { kind: 'connect' }
  /** "Yeni izin gerekiyor · yeniden bağlan" */
  | { kind: 'scopes'; source: Microsoft365Source }
  /** the firm or the team turned it off (the copies stay, MsDurumlar) */
  | { kind: 'off' }
  /** the person's own switch is off (Bağlantılar) */
  | { kind: 'switched_off' }
  /** the viewer's own check: the file is gone, or they cannot see it in Microsoft 365 */
  | { kind: 'gone' }
  | { kind: 'forbidden' };

/**
 * "Yeniden al" in the aside: anyone who may manage the copy, with their own Microsoft account (r2 Q10); offered,
 * disabled with the reason, or not there. Attachments do not change: no button.
 */
export function refetchView(doc: DocumentDto, status: Microsoft365StatusDto | undefined, check?: DocumentSourceCheckDto): RefetchView {
  const source = doc.source;
  if (!source) return { kind: 'hidden' };
  if (source.provider === 'outlook') return { kind: 'outlook' };
  if (!doc.canManage) return { kind: 'hidden' };
  if (fetching(doc)) return { kind: 'busy' };
  // a failed first copy is tried again with "Yeniden dene" (its own route), not here
  if (doc.status !== 'ready' || doc.canRefetch === false) return { kind: 'hidden' };
  // still reading the person's state
  if (!status) return { kind: 'hidden' };
  const feature = SOURCE_FEATURE[source.provider];
  if (!status.available || !status.features[feature]) return { kind: 'off' };
  if (!status.enabled) return { kind: 'switched_off' };
  if (status.state !== 'connected') return { kind: 'connect' };
  if (!status.grants[feature]) return { kind: 'scopes', source: source.provider };
  if (check?.state === 'gone') return { kind: 'gone' };
  if (check?.state === 'forbidden') return { kind: 'forbidden' };
  return { kind: 'ready' };
}

/**
 * The locative ending of each month's spoken name ("28 Ekim'de" → "28 Eki'de", "3 Ocak'ta"): the short name is read as
 * the full one.
 */
const MONTH_LOCATIVE = ['ta', 'ta', 'ta', 'da', 'ta', 'da', 'da', 'ta', 'de', 'de', 'da', 'ta'];

/**
 * When the file changed at its source, in a sentence: "28 Eki'de", "bugün 10:12'de", "dün 23:49'da", "3 Oca 2025'te"
 * (English: "on 28 Oct", "today at 10:12"). The company's days (D-119).
 */
export function changedWhen(iso: string, locale: string, timeZone: string, now: Date, t: Translate): string {
  const at = new Date(iso);
  const days = zonedDaysBetween(at, now, timeZone);
  const time = at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone });
  if (days === 0) return t('documents.when.today', { time, suffix: trLocative(time) });
  if (days === 1) return t('documents.when.yesterday', { time, suffix: trLocative(time) });
  const parts = zonedParts(at, timeZone);
  const sameYear = parts.year === zonedParts(now, timeZone).year;
  const month = at.toLocaleDateString(locale, { month: 'short', timeZone });
  const date = sameYear ? `${parts.day} ${month}` : `${parts.day} ${month} ${parts.year}`;
  const suffix = sameYear ? (MONTH_LOCATIVE[parts.month - 1] ?? 'de') : numberLocative(parts.year);
  return t('documents.when.date', { date, suffix });
}

/** "Kaynaktaki": "28 Eki'de Ayşe Demir değiştirdi"; the viewer's own check is newer than what was stored at the copy. */
export function changedText(source: DocumentSourceDto, check: DocumentSourceCheckDto | undefined, locale: string, timeZone: string, now: Date, t: Translate): string | null {
  const at = check?.modifiedAt ?? source.sourceModifiedAt;
  const by = check?.modifiedAt ? check.modifiedBy : source.sourceModifiedBy;
  if (!at || Number.isNaN(Date.parse(at))) return null;
  const when = changedWhen(at, locale, timeZone, now, t);
  return by ? t('documents.aside.changedBy', { when, by }) : t('documents.aside.changed', { when });
}

/** An attachment of this conversation saved before (the list survives a reload). */
export function savedOf(list: readonly Microsoft365SavedAttachmentDto[] | undefined, ref: string): Microsoft365SavedAttachmentDto | null {
  return list?.find((item) => item.ref === ref) ?? null;
}
